import { Box, Button } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Backdrop, ButtonsContainer } from "./styles";

type Props = {
  rowId: string;
  bundleRowId?: string;
  handleToggleAdditionalInfo: () => void;
  handleDeleteRow: (rowId: string, id?: string, bundleRowId?: string) => void;
  onSave: () => void;
  disabled: boolean;
};

type DeleteState = 0 | 1;
type ButtonVariant = "outlined" | "contained";

const Buttons = ({
  handleToggleAdditionalInfo,
  rowId,
  bundleRowId,
  handleDeleteRow,
  onSave,
  disabled,
}: Props) => {
  const [deleteState, setDeleteState] = useState<DeleteState>(0);

  const { t } = useTranslation("order");

  const detectEsc = useCallback(
    (e: KeyboardEvent) => {
      if ((e.key === "Escape" || e.code === "Escape") && deleteState === 1) {
        setDeleteState(0);
      }
    },
    [deleteState],
  );

  useEffect(() => {
    document.addEventListener("keydown", detectEsc, false);

    return () => {
      document.removeEventListener("keydown", detectEsc, false);
    };
  }, [detectEsc]);

  const states = {
    0: {
      variant: "outlined",
      labelKey: bundleRowId ? "deleteShifts" : "deleteShift",
    },
    1: {
      variant: "contained",
      labelKey: bundleRowId ? "confirmDeleteShifts" : "confirmDeleteShift",
    },
  };

  const handleStateChange = () => {
    if (deleteState === 1) {
      setDeleteState(0);
      handleToggleAdditionalInfo();
      return handleDeleteRow(rowId, undefined, bundleRowId);
    }

    setDeleteState((deleteState + 1) as DeleteState);
  };

  const saveButtonLabel = bundleRowId ? t("saveShifts") : t("saveShift");

  return (
    <>
      {deleteState === 1 && <Backdrop onClick={() => setDeleteState(0)} />}
      <ButtonsContainer sx={{ cursor: "grab" }}>
        <Box display="flex" gap="8px">
          <Button
            sx={{ borderRadius: "30px" }}
            variant="outlined"
            onClick={handleToggleAdditionalInfo}
          >
            {t("common:cancelShort")}
          </Button>
          <Button
            sx={{ borderRadius: "30px" }}
            variant={states[deleteState].variant as ButtonVariant}
            color="error"
            onClick={handleStateChange}
          >
            {t(states[deleteState].labelKey)}
          </Button>
        </Box>
        <Button
          disabled={disabled}
          sx={{ borderRadius: "30px" }}
          variant="contained"
          onClick={onSave}
        >
          {saveButtonLabel}
        </Button>
      </ButtonsContainer>
    </>
  );
};

export default Buttons;
